import type { DialogProps } from '@mui/material/Dialog';
import type { SxProps, Theme } from '@mui/material/styles';

import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { UseDatePickerReturn } from './use-date-picker';

// ----------------------------------------------------------------------

export type DialogDatePickerProps = DialogProps &
  UseDatePickerReturn & { onSubmit?: (data: Dayjs) => void; minDate?: Dayjs };

export function DialogDatePicker({
  open,
  onClose,
  onSubmit,
  minDate,
  /********/
  selectedDate,
  onChangeSelectedDate,
  /********/
  PaperProps,
  variant = 'input',
  title = 'Select date',
  ...other
}: DialogDatePickerProps) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const isCalendarView = variant === 'calendar';

  const [date, setDate] = useState<Dayjs | null>(selectedDate ?? dayjs());

  useEffect(() => {
    if (open) {
      setDate(selectedDate);
    }
  }, [open]);

  const handleSubmit = () => {
    onClose();
    onSubmit?.(date!);
  };

  const contentStyles: SxProps<Theme> = {
    py: 1,
    gap: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    ...(isCalendarView && mdUp && { flexDirection: 'row' }),
  };

  const blockStyles: SxProps<Theme> = {
    borderRadius: 2,
    border: `dashed 1px ${theme.vars.palette.divider}`,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isCalendarView ? false : 'xs'}
      PaperProps={{
        ...PaperProps,
        sx: [
          { ...(isCalendarView && { maxWidth: 720 }) },
          ...(Array.isArray(PaperProps?.sx) ? (PaperProps?.sx ?? []) : [PaperProps?.sx]),
        ],
      }}
      {...other}
    >
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

      <DialogContent sx={{ ...(isCalendarView && mdUp && { overflow: 'unset' }) }}>
        <Box sx={contentStyles}>
          {isCalendarView ? (
            <Box sx={blockStyles}>
              <DateCalendar value={date} minDate={minDate} onChange={setDate} />
            </Box>
          ) : (
            <DatePicker
              label="Start date"
              value={date}
              minDate={minDate}
              onChange={(v) => setDate(v)}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={!date} onClick={handleSubmit}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
